import { useEffect, useState } from 'react'

interface ShareDialogProps {
  open: boolean
  title: string
  onClose: () => void
}

export default function ShareDialog({ open, title, onClose }: ShareDialogProps) {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    setCopied(false)
    setCopyFailed(false)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  const shareUrl = window.location.href

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setCopyFailed(false)
    } catch {
      setCopyFailed(true)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-primary-950/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="分享作品"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-background-200 bg-background-50 p-5 md:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">分享《{title}》</h2>
            <p className="mt-1 text-xs leading-5 text-foreground-500">
              复制页面链接后即可分享给朋友，链接支持直接打开详情页。
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭分享弹窗"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-800"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
          <i className="ri-link text-sm text-foreground-500" />
          <input
            readOnly
            value={shareUrl}
            aria-label="作品页面链接"
            className="w-full bg-transparent text-xs text-foreground-800 focus:outline-none"
          />
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={handleCopy}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-800 px-4 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
            {copied ? '链接已复制' : '复制链接'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
          >
            关闭
          </button>
        </div>

        {copyFailed && (
          <p className="mt-3 text-xs text-accent-800">
            当前浏览器限制了自动复制，请手动选中上方链接复制。
          </p>
        )}

        <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-5 text-foreground-400">
          <i className="ri-information-line mt-0.5" />
          分享内容为影视作品信息页，不含任何真实播放资源。
        </p>
      </div>
    </div>
  )
}